import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
	VehicleListDto,
	VehicleRecordDto,
	VehiclesAppOptionsDto,
	VehicleSearchDto,
	VehiclesFilters,
	VehiclesStatsDto,
	VehicleStatusEnum,
	vehicleStatusToColor,
	vehicleValidators,
	CreateVehicleDto,
	UpdateVehicleDto,
	ModulesEnum,
	TagRecordCategoryEnum,
	VehicleTypeEnum,
} from '@splitsecondconnect/ultramarine-shared';
import { v4 as uuidv4 } from 'uuid';

import { CommentsSectionComponent } from 'app/shared/components/comments-section/comments-section.component';
import { VehicleExtraFeaturesSectionComponent } from 'app/shared/components/sections/vehicle-extra-features-section/vehicle-extra-features-section.component';
import { TypeSectionComponent } from 'app/shared/components/type-section/type-section.component';

import { RecordTabsComponent } from '../../../../layout/components/record-tabs/record-tabs.component';
import { TabContentDirective } from '../../../../layout/components/record-tabs/tab-content.directive';
import { BaseRecordComponent } from '../../../../shared/base-classes/base-record.component';
import { AnnotationsSectionComponent } from '../../../../shared/components/annotations-section/annotations-section.component';
import { ContextInfoComponent } from '../../../../shared/components/context-info/context-info.component';
import { ErrorsBoxComponent } from '../../../../shared/components/errors-box/errors-box.component';
import { RecordFooterComponent } from '../../../../shared/components/footers/record-footer/record-footer.component';
import { RecordHeaderComponent } from '../../../../shared/components/headers/record-header/record-header.component';
import { HeaderDataModel } from '../../../../shared/components/headers/record-header/record-header.model';
import { MoreDetailsComponent } from '../../../../shared/components/more-details/more-details.component';
import { NoActivityComponent } from '../../../../shared/components/no-activity.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { UltraPrimaryButtonComponent } from '../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { SectionMetadataDirective } from '../../../../shared/directives/section-metadata.directive';
import { ADMINISTRATION_RECORD_TABS } from '../../../../shared/enums/administration-record-tabs';
import { dateCast } from '../../../../shared/functions/sectionCasters.function';
import {
	mapBaseDtoToBaseModel,
	mapBaseModelToBaseDto,
} from '../../../../shared/mapper/base.mapper';
import { mapFeatureItemDtoToFeatureItemModel } from '../../../../shared/mapper/feature-items.mapper';
import {
	mapFilesDtoToModel,
	mapFilesModelToDto,
} from '../../../../shared/mapper/files.mapper';
import {
	mapTagDtoToModel,
	mapTagModelToDto,
} from '../../../../shared/mapper/tags.mapper';
import { GetIconFromOptionsPipe } from '../../../../shared/pipes/get-icon-from-options.pipe';
import { MODULE_SELECTOR_STRATEGY_TOKEN } from '../../../../shared/tokens/module-selector.token';
import { TAGS_RECORD_CATEGORY_TOKEN } from '../../../../shared/tokens/tags-record-category.token';
import { VEHICLES_MODULE_SERVICE } from '../../../../shared/tokens/vehicles-module-service.token';
import { LocationSectionComponent } from '../../locations/sections/location-section/location-section.component';
import { SupplierSectionComponent } from '../../suppliers/sections/supplier-section/supplier-section.component';
import { VehicleCategorySectionComponent } from '../../vehicle-categories/sections/vehicle-category-section/vehicle-category-section.component';
import { VehiclesListPortalComponent } from '../components/vehicles-list-item-menu/vehicles-list-item-menu.component';
import { VehicleModuleSelectorStrategy } from '../config/vehicle-module-selector-strategy';
import { VehicleRecordModel } from '../models/vehicle-record.model';
import { VEHICLE_TYPE } from '../translations/vehicles-select-items';
import { VehiclesService } from '../vehicles.service';

import { VehicleActionsComponent } from './components/vehicle-actions/vehicle-actions.component';
import { VehicleNextOperationComponent } from './components/vehicle-next-operation/vehicle-next-operation.component';
import { VehiclesColorComponent } from './components/vehicles-color/vehicles-color.component';
import { VehiclesDetailsComponent } from './components/vehicles-details/vehicles-details.component';
import { VehiclesEmissionsComponent } from './components/vehicles-emissions/vehicles-emissions.component';
import { VehiclesEngineComponent } from './components/vehicles-engine/vehicles-engine.component';
import { VehiclesMakeModelComponent } from './components/vehicles-make-model/vehicles-make-model.component';
import { VehiclesMotorComponent } from './components/vehicles-motor/vehicles-motor.component';
import { VehiclesPowerPlantComponent } from './components/vehicles-power-plant/vehicles-power-plant.component';
import { VehiclesPurchaseDetailsComponent } from './components/vehicles-purchase-details/vehicles-purchase-details.component';
import { VehiclesSummaryComponent } from './components/vehicles-summary/vehicles-summary.component';

@Component({
	selector: 'app-vehicles-record',
	templateUrl: 'vehicles-record.component.html',
	imports: [
		VehiclesSummaryComponent,
		VehiclesDetailsComponent,
		VehiclesPurchaseDetailsComponent,
		LocationSectionComponent,
		VehiclesPowerPlantComponent,
		VehiclesMakeModelComponent,
		VehiclesColorComponent,
		VehiclesEngineComponent,
		VehiclesMotorComponent,
		VehiclesEmissionsComponent,
		SupplierSectionComponent,
		VehiclesListPortalComponent,
		VehicleCategorySectionComponent,
		AnnotationsSectionComponent,
		RecordTabsComponent,
		AsyncPipe,
		TabContentDirective,
		IonicModule,
		VehicleActionsComponent,
		NoActivityComponent,
		RecordHeaderComponent,
		RecordFooterComponent,
		MoreDetailsComponent,
		TypeSectionComponent,
		CommentsSectionComponent,
		VehicleNextOperationComponent,
		StepperComponent,
		SectionMetadataDirective,
		ContextInfoComponent,
		ErrorsBoxComponent,
		UltraPrimaryButtonComponent,
		NgTemplateOutlet,
		VehicleExtraFeaturesSectionComponent,
		GetIconFromOptionsPipe,
	],
	host: {
		'[class]': 'modalClass()',
	},
	providers: [
		{
			provide: MODULE_SELECTOR_STRATEGY_TOKEN,
			useClass: VehicleModuleSelectorStrategy,
		},
		{
			provide: TAGS_RECORD_CATEGORY_TOKEN,
			useValue: TagRecordCategoryEnum.VEHICLE,
		},
		{
			provide: VEHICLES_MODULE_SERVICE,
			useExisting: VehiclesService,
		},
	],
})
export class VehiclesRecordComponent extends BaseRecordComponent<
	VehicleRecordDto,
	CreateVehicleDto,
	UpdateVehicleDto,
	VehicleListDto,
	VehiclesStatsDto,
	VehiclesAppOptionsDto,
	VehiclesFilters,
	VehicleRecordModel,
	VehicleStatusEnum,
	VehicleSearchDto
> {
	constructor(
		private _vehiclesService: VehiclesService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _changeDetectorRef: ChangeDetectorRef,
	) {
		super(_vehiclesService, _router, _activatedRoute, _changeDetectorRef);
		this.module = ModulesEnum.VEHICLES;
	}
	vehicleTypeEnum = VehicleTypeEnum;
	recordTabs = ADMINISTRATION_RECORD_TABS;
	typeOptions = VEHICLE_TYPE;

	protected _isRecordFormValid(): boolean {
		const validationResult = vehicleValidators.canCreateOrUpdate(
			this._mapModelToUpdateDto(this.recordData()),
		);
		this.errorMessages.next(validationResult.errors);
		return validationResult.isValid;
	}

	protected _updateHeaderData(
		record: VehicleRecordModel,
	): HeaderDataModel<VehicleStatusEnum> {
		return {
			...this._updateHeaderDataBase(record),
			title: record.licensePlate ?? record.vin,
			status: record.status,

			color: vehicleStatusToColor(record.status),
		};
	}

	protected _mapDtoToModel(dto: VehicleRecordDto): VehicleRecordModel {
		return {
			...mapBaseDtoToBaseModel(dto),
			...mapTagDtoToModel(dto.tags),
			uuid: dto.uuid ?? null,
			vin: dto.vin ?? null,
			notes: dto.notes ?? null,
			engineNumber: dto.engineNumber ?? null,
			licensePlate: dto.licensePlate ?? null,
			registeredAt: dateCast(dto.registeredAt),
			deregisteredAt: dateCast(dto.deregisteredAt),
			makeId: dto.makeId ?? null,
			modelId: dto.modelId ?? null,
			year: dto.year ?? null,
			seats: dto.seats ?? null,
			series: dto.series ?? null,
			hasGpsTracker: dto.hasGpsTracker ?? null,
			gpsTrackerUuid: dto.gpsTrackerUuid ?? null,
			makeFull: dto.makeFull ?? null,
			trim: dto.trim ?? null,
			manufacturedAt: dateCast(dto.manufacturedAt),
			exteriorColorId: dto.exteriorColorId ?? null,
			interiorColorId: dto.interiorColorId ?? null,
			bodyType: dto.bodyType ?? null,
			fuelType: dto.fuelType ?? null,
			type: dto.type ?? null,
			steeringPosition: dto.steeringPosition ?? null,
			doors: dto.doors ?? null,
			transmissionType: dto.transmissionType ?? null,
			driveTrainType: dto.driveTrainType ?? null,
			odometerUnit: dto.odometerUnit ?? null,
			odometerValue: dto.lastOdometerTracking?.value ?? null,
			primaryPowerPlantType: dto.primaryPowerPlantType ?? null,
			secondaryPowerPlantType: dto.secondaryPowerPlantType ?? null,
			engineSizeUnit: dto.engineSizeUnit ?? null,
			engineSize: dto.engineSize ?? null,
			engineOutput: dto.engineOutput ?? null,
			engineTurbine: dto.engineTurbine ?? null,
			averageEmissionsCO2: dto.averageEmissionsCO2 ?? null,
			emissionsUnitCO2: dto.emissionsUnitCO2 ?? null,
			averageFuelConsumption: dto.averageFuelConsumption ?? null,
			fuelConsumptionUnit: dto.fuelConsumptionUnit ?? null,
			fuelTankCapacity: dto.fuelTankCapacity ?? null,
			fuelTankCapacityUnit: dto.fuelTankCapacityUnit ?? null,
			averageElectricConsumption: dto.averageElectricConsumption ?? null,
			electricBatteryCapacity: dto.electricBatteryCapacity ?? null,
			electricBatteryVoltage: dto.electricBatteryVoltage ?? null,
			electricBatteryType: dto.electricBatteryType ?? null,
			electricMotorOutput: dto.electricMotorOutput ?? null,
			electricMotorRangeFrom: dto.electricMotorRangeFrom ?? null,
			electricMotorRangeTo: dto.electricMotorRangeTo ?? null,
			status: dto.status ?? null,
			statusTracking: dto.statusTracking ?? null,
			allocation: dto.allocation ?? null,
			locationId: dto.locationId ?? null,
			supplierId: dto.supplierId ?? null,
			vehicleCategoryId: dto.vehicleCategoryId ?? null,
			purchasedAt: dateCast(dto.purchasedAt),
			purchaseListPrice: dto.purchaseListPrice ?? null,
			purchasePaidPrice: dto.purchasePaidPrice ?? null,
			isTransferable: dto.isTransferable ?? false,
			...mapFilesDtoToModel(dto.files),
			...mapFeatureItemDtoToFeatureItemModel(dto.featureItems),
		};
	}

	protected _mapModelToDto(model: VehicleRecordModel): VehicleRecordDto {
		return {
			...mapBaseModelToBaseDto(model),
			...mapTagModelToDto(model.tags),
			uuid: model.uuid ?? null,
			vin: model.vin ?? null,
			status: model.status ?? null,
			notes: model.notes ?? null,
			engineNumber: model.engineNumber ?? null,
			licensePlate: model.licensePlate ?? null,
			registeredAt: dateCast(model.registeredAt),
			deregisteredAt: dateCast(model.deregisteredAt),
			makeId: model.makeId ?? null,
			modelId: model.modelId ?? null,
			year: model.year ?? null,
			seats: model.seats ?? null,
			hasGpsTracker: model.hasGpsTracker ?? null,
			gpsTrackerUuid: model.gpsTrackerUuid ?? null,
			type: model.type ?? null,
			series: model.series ?? null,
			trim: model.trim ?? null,
			manufacturedAt: dateCast(model.manufacturedAt),
			exteriorColorId: model.exteriorColorId ?? null,
			interiorColorId: model.interiorColorId ?? null,
			bodyType: model.bodyType ?? null,
			fuelType: model.fuelType ?? null,
			steeringPosition: model.steeringPosition ?? null,
			doors: model.doors ?? null,
			transmissionType: model.transmissionType ?? null,
			driveTrainType: model.driveTrainType ?? null,
			odometerUnit: model.odometerUnit ?? null,
			primaryPowerPlantType: model.primaryPowerPlantType ?? null,
			secondaryPowerPlantType: model.secondaryPowerPlantType ?? null,
			engineSizeUnit: model.engineSizeUnit ?? null,
			engineSize: model.engineSize ?? null,
			engineOutput: model.engineOutput ?? null,
			engineTurbine: model.engineTurbine ?? false,
			averageEmissionsCO2: model.averageEmissionsCO2 ?? null,
			emissionsUnitCO2: model.emissionsUnitCO2 ?? null,
			averageFuelConsumption: model.averageFuelConsumption ?? null,
			fuelConsumptionUnit: model.fuelConsumptionUnit ?? null,
			fuelTankCapacity: model.fuelTankCapacity ?? null,
			fuelTankCapacityUnit: model.fuelTankCapacityUnit ?? null,
			averageElectricConsumption: model.averageElectricConsumption ?? null,
			electricBatteryCapacity: model.electricBatteryCapacity ?? null,
			electricBatteryVoltage: model.electricBatteryVoltage ?? null,
			electricBatteryType: model.electricBatteryType ?? null,
			electricMotorOutput: model.electricMotorOutput ?? null,
			electricMotorRangeFrom: model.electricMotorRangeFrom ?? null,
			electricMotorRangeTo: model.electricMotorRangeTo ?? null,
			allocation: model.allocation ?? null,
			locationId: model.locationId ?? null,
			supplierId: model.supplierId ?? null,
			vehicleCategoryId: model.vehicleCategoryId ?? null,
			purchasedAt: dateCast(model.purchasedAt),
			purchaseListPrice: model.purchaseListPrice ?? null,
			purchasePaidPrice: model.purchasePaidPrice ?? null,
			isTransferable: model.isTransferable ?? null,
			...mapFilesModelToDto(model.files),
		};
	}

	protected _mapModelToCreateDto(model: VehicleRecordModel): CreateVehicleDto {
		const uuid = uuidv4();
		return {
			...mapBaseModelToBaseDto(model),
			...mapTagModelToDto(model.tags),
			uuid: uuid,
			vin: model.vin ?? null,
			notes: model.notes ?? null,
			engineNumber: model.engineNumber ?? null,
			licensePlate: model.licensePlate ?? null,
			registeredAt: dateCast(model.registeredAt),
			deregisteredAt: dateCast(model.deregisteredAt),
			makeId: model.makeId ?? null,
			modelId: model.modelId ?? null,
			year: model.year ?? null,
			seats: model.seats ?? null,
			hasGpsTracker: model.hasGpsTracker ?? null,
			gpsTrackerUuid: model.gpsTrackerUuid ?? null,
			type: model.type ?? null,
			series: model.series ?? null,
			trim: model.trim ?? null,
			manufacturedAt: dateCast(model.manufacturedAt),
			exteriorColorId: model.exteriorColorId ?? null,
			interiorColorId: model.interiorColorId ?? null,
			bodyType: model.bodyType ?? null,
			fuelType: model.fuelType ?? null,
			steeringPosition: model.steeringPosition ?? null,
			doors: model.doors ?? null,
			transmissionType: model.transmissionType ?? null,
			driveTrainType: model.driveTrainType ?? null,
			odometerUnit: model.odometerUnit ?? null,
			primaryPowerPlantType: model.primaryPowerPlantType ?? null,
			secondaryPowerPlantType: model.secondaryPowerPlantType ?? null,
			engineSizeUnit: model.engineSizeUnit ?? null,
			engineSize: model.engineSize ?? null,
			engineOutput: model.engineOutput ?? null,
			engineTurbine: model.engineTurbine ?? false,
			averageEmissionsCO2: model.averageEmissionsCO2 ?? null,
			emissionsUnitCO2: model.emissionsUnitCO2 ?? null,
			averageFuelConsumption: model.averageFuelConsumption ?? null,
			fuelConsumptionUnit: model.fuelConsumptionUnit ?? null,
			fuelTankCapacity: model.fuelTankCapacity ?? null,
			fuelTankCapacityUnit: model.fuelTankCapacityUnit ?? null,
			averageElectricConsumption: model.averageElectricConsumption ?? null,
			electricBatteryCapacity: model.electricBatteryCapacity ?? null,
			electricBatteryVoltage: model.electricBatteryVoltage ?? null,
			electricBatteryType: model.electricBatteryType ?? null,
			electricMotorOutput: model.electricMotorOutput ?? null,
			electricMotorRangeFrom: model.electricMotorRangeFrom ?? null,
			electricMotorRangeTo: model.electricMotorRangeTo ?? null,
			allocation: model.allocation ?? null,
			locationId: model.locationId ?? null,
			supplierId: model.supplierId ?? null,
			vehicleCategoryId: model.vehicleCategoryId ?? null,
			purchasedAt: dateCast(model.purchasedAt),
			purchaseListPrice: model.purchaseListPrice ?? null,
			purchasePaidPrice: model.purchasePaidPrice ?? null,
			isTransferable: model.isTransferable ?? null,
			...mapFilesModelToDto(model.files),
			featureItems: model.featureItems?.map((item) => item.id) ?? null,
		};
	}

	protected _mapModelToUpdateDto(model: VehicleRecordModel): UpdateVehicleDto {
		return {
			...mapBaseModelToBaseDto(model),
			...mapTagModelToDto(model.tags),
			uuid: model.uuid ?? null,
			vin: model.vin ?? null,
			notes: model.notes ?? null,
			engineNumber: model.engineNumber ?? null,
			licensePlate: model.licensePlate ?? null,
			registeredAt: dateCast(model.registeredAt),
			deregisteredAt: dateCast(model.deregisteredAt),
			makeId: model.makeId ?? null,
			modelId: model.modelId ?? null,
			year: model.year ?? null,
			seats: model.seats ?? null,
			hasGpsTracker: model.hasGpsTracker ?? null,
			gpsTrackerUuid: model.gpsTrackerUuid ?? null,
			type: model.type ?? null,
			series: model.series ?? null,
			trim: model.trim ?? null,
			manufacturedAt: dateCast(model.manufacturedAt),
			exteriorColorId: model.exteriorColorId ?? null,
			interiorColorId: model.interiorColorId ?? null,
			bodyType: model.bodyType ?? null,
			fuelType: model.fuelType ?? null,
			steeringPosition: model.steeringPosition ?? null,
			doors: model.doors ?? null,
			transmissionType: model.transmissionType ?? null,
			driveTrainType: model.driveTrainType ?? null,
			odometerUnit: model.odometerUnit ?? null,
			primaryPowerPlantType: model.primaryPowerPlantType ?? null,
			secondaryPowerPlantType: model.secondaryPowerPlantType ?? null,
			engineSizeUnit: model.engineSizeUnit ?? null,
			engineSize: model.engineSize ?? null,
			engineOutput: model.engineOutput ?? null,
			engineTurbine: model.engineTurbine ?? false,
			averageEmissionsCO2: model.averageEmissionsCO2 ?? null,
			emissionsUnitCO2: model.emissionsUnitCO2 ?? null,
			averageFuelConsumption: model.averageFuelConsumption ?? null,
			fuelConsumptionUnit: model.fuelConsumptionUnit ?? null,
			fuelTankCapacity: model.fuelTankCapacity ?? null,
			fuelTankCapacityUnit: model.fuelTankCapacityUnit ?? null,
			averageElectricConsumption: model.averageElectricConsumption ?? null,
			electricBatteryCapacity: model.electricBatteryCapacity ?? null,
			electricBatteryVoltage: model.electricBatteryVoltage ?? null,
			electricBatteryType: model.electricBatteryType ?? null,
			electricMotorOutput: model.electricMotorOutput ?? null,
			electricMotorRangeFrom: model.electricMotorRangeFrom ?? null,
			electricMotorRangeTo: model.electricMotorRangeTo ?? null,
			allocation: model.allocation ?? null,
			locationId: model.locationId ?? null,
			supplierId: model.supplierId ?? null,
			vehicleCategoryId: model.vehicleCategoryId ?? null,
			purchasedAt: dateCast(model.purchasedAt),
			purchaseListPrice: model.purchaseListPrice ?? null,
			purchasePaidPrice: model.purchasePaidPrice ?? null,
			isTransferable: model.isTransferable ?? null,
			...mapFilesModelToDto(model.files),
			featureItems: model.featureItems?.map((item) => item.id) ?? null,
		};
	}
}
